"use client";

import { Suspense, ComponentType, useEffect, useState } from "react";
import { Any } from "@cede/types";
import { cn } from "@/lib/utils";
import { transition } from "@/styles/utils";

interface DeferredModalProps {
  open: boolean;
  component: ComponentType<Any>;
  fallback?: React.ReactNode;
  [key: string]: Any;
}

/**
 * DeferredModal - mounts a modal only after it's requested to open, but keeps the
 * code eagerly bundled to avoid dynamic import complexity.
 */
export const DeferredModal = ({ open, component: Component, fallback = null, ...props }: DeferredModalProps) => {
  const [hasMountedOnce, setHasMountedOnce] = useState(false);
  const [isComponentReady, setIsComponentReady] = useState(false);

  useEffect(() => {
    if (open && !hasMountedOnce) {
      setHasMountedOnce(true);
      setIsComponentReady(true);
    } else if (hasMountedOnce) {
      setIsComponentReady(true);
    }
  }, [open, hasMountedOnce]);

  if (!hasMountedOnce || !Component) return null;

  const effectiveOpen = isComponentReady && open;

  return (
    <div
      className={cn(transition(), isComponentReady && open ? "opacity-100" : "opacity-0")}
      style={{
        position: "absolute",
        width: 0,
        height: 0,
        overflow: "hidden",
        pointerEvents: effectiveOpen ? "auto" : "none",
      }}
    >
      <Suspense fallback={fallback}>
        <Component open={effectiveOpen} {...props} />
      </Suspense>
    </div>
  );
};
